import moment from 'moment';
import React, { useState } from 'react';
import { Navigate, ToolbarProps } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const CustomCalendarToolBar: React.FC<any> = (props: ToolbarProps) => {

    let lang = localStorage.getItem("i18nextLng")?.split('-')[0] ?? "en";
    moment.locale(lang);

    const [t] = useTranslation();
    const [viewState, setViewState] = useState<'week' | 'day'>('week');

    // Convert Moment format tokens (DD/MM/YYYY) to date-fns tokens (dd/MM/yyyy)
    const dateFnsFormat = moment.localeData().longDateFormat('L')
        .replace(/YYYY/g, 'yyyy')
        .replace(/YY/g, 'yy')
        .replace(/DD/g, 'dd')
        .replace(/D/g, 'd');

    // Only pass locale if it has been registered via registerLocale (currently only 'es')
    const registeredLocale = lang === 'es' ? 'es' : undefined;

    const goToDayView = () => {
        props.onView('day');
        setViewState('day');
    };

    const goToWeekView = () => {
        props.onView('week');
        setViewState('week');
    };

    const goNext = () => {
        props.onNavigate(Navigate.NEXT);
    };

    const goPrevious = () => {
        props.onNavigate(Navigate.PREVIOUS);
    };

    const goToSpecificDate = (date: Date | null) => {
        if (date) {
            props.onNavigate(Navigate.DATE, date);
        }
    };

    const getLabel = () => {
        if (viewState === 'week') {
            const start = moment(props.date).startOf('week');
            const end = moment(props.date).endOf('week');
            if (start.month() === end.month()) {
                return `${start.format('D')} – ${end.format('D')} ${start.format('MMMM YYYY')}`;
            }
            return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
        }
        return moment(props.date).format('dddd, D MMMM YYYY');
    };

    return (
        <div className="rbc-toolbar">
            {/* Left: date picker + today */}
            <div className="rbc-toolbar-nav">
                <div className="rbc-toolbar-date-picker">
                    <DatePicker
                        selected={props.date}
                        onChange={goToSpecificDate}
                        dateFormat={dateFnsFormat}
                        locale={registeredLocale}
                        todayButton={t('Today')}
                        showPopperArrow={false}
                        popperPlacement="bottom-start"
                        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                            <div className="rbc-datepicker-header">
                                <button className="rbc-datepicker-nav-btn" onClick={decreaseMonth}>&#9664;</button>
                                <span className="rbc-datepicker-month-label">
                                    {moment(date).format('MMMM YYYY')}
                                </span>
                                <button className="rbc-datepicker-nav-btn" onClick={increaseMonth}>&#9654;</button>
                            </div>
                        )}
                    />
                </div>
                {/* <button
                    className="rbc-toolbar-btn rbc-toolbar-today-btn"
                    onClick={goToToday}
                    title={t('Today')}
                >
                    {t('Today')}
                </button> */}
            </div>

            {/* Center: prev / label / next */}
            <div className="rbc-toolbar-label">
                <button
                    className="rbc-toolbar-btn"
                    onClick={goPrevious}
                    title={t('Previous')}
                >
                    <i className="iconsminds-arrow-back-3" />
                </button>
                <span>{getLabel()}</span>
                <button
                    className="rbc-toolbar-btn"
                    onClick={goNext}
                    title={t('Next')}
                >
                    <i className="iconsminds-arrow-forward-2" />
                </button>
            </div>

            {/* Right: week / day toggle */}
            <div className="rbc-toolbar-views">
                <button
                    className={`rbc-toolbar-view-btn${viewState === 'week' ? ' rbc-toolbar-view-btn--active' : ''}`}
                    onClick={goToWeekView}
                >
                    {t('Week')}
                </button>
                <button
                    className={`rbc-toolbar-view-btn${viewState === 'day' ? ' rbc-toolbar-view-btn--active' : ''}`}
                    onClick={goToDayView}
                >
                    {t('Day')}
                </button>
            </div>
        </div>
    );

};

export default React.memo(CustomCalendarToolBar);
